// Shared timestamp helpers for a single-shop app — every display shows the
// shop's own wall-clock time (Australia/Melbourne), not whatever zone the
// viewing device happens to be configured for. Same reasoning as
// components/Clock.jsx already hardcoding this rather than trusting the
// browser's ambient zone (confirmed live: a real user's browser resolved
// its own "local" zone to UTC, not Melbourne, even though they're
// physically there).
export const SHOP_TZ = 'Australia/Melbourne';

// Server timestamps are 'YYYY-MM-DD HH:MM:SS' UTC with no timezone suffix.
// A plain `new Date(iso)` on that shape is parsed by the non-standard
// space-separated fallback, which V8 treats as *already local* — no
// conversion happens, so the raw UTC clock reading displays as if it were
// local. Reattaching a 'T' and 'Z' makes the string spec-compliant UTC.
// Exported for callers that need the raw instant (e.g. a live countdown),
// not just a formatted string.
export const parseUtc = (iso) => new Date(`${iso.replace(' ', 'T')}Z`);

export const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = parseUtc(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { timeZone: SHOP_TZ, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
};

export const formatTime = (iso) => {
  if (!iso) return '—';
  const d = parseUtc(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('en-AU', { timeZone: SHOP_TZ, hour: '2-digit', minute: '2-digit', hour12: false });
};

export const formatDateShort = (iso) => {
  if (!iso) return '—';
  const d = parseUtc(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { timeZone: SHOP_TZ, day: 'numeric', month: 'short' });
};

const pad = (n) => String(n).padStart(2, '0');

// Numeric Y/M/D/H/M/S of a UTC instant, as seen in SHOP_TZ. Intl resolves
// the correct DST offset for the given date internally — no fixed +10/+11.
const shopParts = (d) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SHOP_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type).value;
  // Some ICU versions render midnight as "24" with hour12:false.
  const hour = get('hour') === '24' ? '00' : get('hour');
  return { year: get('year'), month: get('month'), day: get('day'), hour, minute: get('minute'), second: get('second') };
};

// Server UTC string -> <input type="datetime-local"> value, converted to
// SHOP_TZ wall-clock time (what the input should show regardless of the
// device's own configured zone).
export const toInputValue = (iso) => {
  if (!iso) return '';
  const d = parseUtc(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = shopParts(d);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// How far SHOP_TZ is ahead of UTC (ms) at the instant `d`, DST-aware:
// format `d` in SHOP_TZ, re-read those numbers as if they were UTC, and
// diff against `d` itself.
const shopOffsetMs = (d) => {
  const p = shopParts(d);
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
  return asUtc - d.getTime();
};

// <input type="datetime-local"> value (SHOP_TZ wall-clock, no tz suffix) ->
// server UTC string. Reading the value as literal UTC gives an anchor
// within one offset-width of the true instant; subtracting the offset at
// that anchor converges on the correct instant (DST-correct except within
// the rare hour of an actual transition, which happens at 2–3am local).
export const fromInputValue = (v) => {
  if (!v) return null;
  const anchor = new Date(`${v}:00Z`);
  if (Number.isNaN(anchor.getTime())) return null;
  const actual = new Date(anchor.getTime() - shopOffsetMs(anchor));
  return `${actual.getUTCFullYear()}-${pad(actual.getUTCMonth() + 1)}-${pad(actual.getUTCDate())} ${pad(actual.getUTCHours())}:${pad(actual.getUTCMinutes())}:${pad(actual.getUTCSeconds())}`;
};

// 'YYYY-MM-DD' calendar date, Melbourne-local, for a given instant (or now).
export const shopDateString = (d = new Date()) => {
  const p = shopParts(d);
  return `${p.year}-${p.month}-${p.day}`;
};
